import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { isStringNullOrWhiteSpace } from 'src/app/util/StringUtil';
import { ExpressionRunResultModel } from '../model/expressionRunResult.model';
import { ExpressionResponse, ExpressionRuntimeArgumentsModel } from '../model/expressions.model';
import { HttpErrorHandler } from './error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class AAExpressionService {

  headers: HttpHeaders;
  apiServer: string;

  constructor(private http: HttpClient, private apiRef: ApiProviderService,
    private messageService: MessageService, private errorHandler: HttpErrorHandler) {
    this.headers = new HttpHeaders();
    this.headers.append('Content-Type', 'application/json');
    this.headers.append('Accept', 'application/json');
    this.apiServer = 'api/ExpressionJs/';
  }


  getExpressionJsScriptOnLoad(viewId: string, module: string, permissionLevel: string, permissionValue: string, callerId: string):
    Observable<ExpressionResponse> {
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}GetExpressionsForOnLoad`,
      [{ Key: 'module', Value: module },
      { Key: 'viewId', Value: viewId },
      { Key: 'permissionLevel', Value: permissionLevel },
      { Key: 'permissionValue', Value: permissionValue },
      { Key: 'callerId', Value: callerId }])
      , { headers: this.headers })
      .pipe(map(json => json as ExpressionResponse));
  }

  getExpressionJsScriptOnSubmit(viewId: string, module: string, permissionLevel: string, permissionValue: string, callerId: string):
    Observable<ExpressionResponse> {
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}GetExpressionsForOnSubmit`,
      [{ Key: 'module', Value: module },
      { Key: 'viewId', Value: viewId },
      { Key: 'permissionLevel', Value: permissionLevel },
      { Key: 'permissionValue', Value: permissionValue },
      { Key: 'callerId', Value: callerId }])
      , { headers: this.headers })
      .pipe(catchError(this.errorHandler.handleError))
      .pipe(map(json => json as ExpressionResponse));
  }

  getExpressionJsScriptForExecute(viewId: string, module: string, permissionLevel: string, permissionValue: string, callerId: string):
    Observable<ExpressionResponse[]> {
    return this.http.get(this.apiRef.getApiUrl(`${this.apiServer}GetExpressionsForExecute`,
      [{ Key: 'module', Value: module },
      { Key: 'viewId', Value: viewId },
      { Key: 'permissionLevel', Value: permissionLevel },
      { Key: 'permissionValue', Value: permissionValue },
      { Key: 'callerId', Value: callerId }])
      , { headers: this.headers })
      .pipe(catchError(this.errorHandler.handleError))
      .pipe(map(json => json as ExpressionResponse[]));
  }

  invokeExpression(expressionData: ExpressionResponse, pageflowFormGroup: UntypedFormGroup, fireObject: HTMLElement = null): Observable<void> {
    if (fireObject) {
      fireObject.focus();
    }

    if (expressionData && expressionData.inputFields) {
      //RunExpression
      return this.http
        .post(this.apiRef.getApiUrl(`${this.apiServer}RunExpression`), expressionData, { headers: this.headers })
        .pipe(
          catchError(this.errorHandler.handleError),
          map((resp) => {
            if (resp) {
              let exResult: ExpressionRunResultModel = null;
              try {
                exResult = JSON.parse(resp.toString());
              } catch (ex) { //AA will return a plaintext error in some circumstances
                console.log(`Error executing expression, invalid response: ${resp}`);
                return;
              }
              if (exResult) {
                const executeFieldName: ExpressionRuntimeArgumentsModel = expressionData.args && expressionData.args.executeFieldVariableKey
                  ? expressionData.args.executeFieldVariableKey : null;

                this.handleResult(exResult, executeFieldName, pageflowFormGroup);
              }

            }
          }));
    } else {
      return of(); //skip and return void
    }
  }

  handleResult(result: ExpressionRunResultModel, executeFieldName: ExpressionRuntimeArgumentsModel, pageflowFormGroup: UntypedFormGroup) {
    if (!result || !result.fields || result.fields.length === 0) {
      return;
    }

    for (const oneResult of result.fields) {
      const formControl = pageflowFormGroup.controls[oneResult.name];

      if (!formControl) {
        console.log('ExpressionJs: unable to find control: ' + oneResult.name);
      } else {

        if (oneResult.value && oneResult.value !== '') {
          formControl.patchValue(oneResult.value);
        }

        if (oneResult.hidden !== null) {
          formControl.controlData.hidden = oneResult.hidden;
        }

        if (oneResult.readOnly !== null) {
          formControl.controlData.readOnly = oneResult.readOnly;
        }

        if (oneResult.required !== null) {
          formControl.controlData.required = oneResult.required;
        }

        if (!isStringNullOrWhiteSpace(oneResult.message)) {
          this.messageService.add({
            severity: 'info',
            summary: `${oneResult.name}: ${oneResult.message}`,
            detail: 'Via ExpressionService',
            closable: false
          });
        }
      }

    }

  }


  isYes(fieldValue, value) {
    if (typeof (value) != 'string' || isStringNullOrWhiteSpace(fieldValue) || isStringNullOrWhiteSpace(value)) {
      return false;
    }

    const checkValue = value.toUpperCase();

    if (checkValue === 'CHECK'
      || checkValue === 'CHECKED'
      || checkValue === 'TRUE'
      || checkValue === 'SELECT'
      || checkValue === 'SELECTED'
      || checkValue === 'ON'
      || checkValue === 'YES'
      || checkValue === 'Y') {
      value = 'Yes';
    } else if (checkValue !== 'FEMALE'
      && checkValue !== 'F'
      && checkValue !== 'MALE'
      && checkValue !== 'M') {
      value = 'No';
    }

    return fieldValue.substring(0, 1).toUpperCase() === value.substring(0, 1).toUpperCase();
  }

}
